export type AgentPhase =
  | 'idle'
  | 'planning'
  | 'streaming'
  | 'applying'
  | 'verifying'
  | 'paused'
  | 'completed'
  | 'cancelled'
  | 'error'

export type AgentStepStatus = 'queued' | 'running' | 'done'

export interface AgentStepState {
  id: string
  label: string
  status: AgentStepStatus
}

export interface AgentMachineState {
  phase: AgentPhase
  resumePhase: Exclude<AgentPhase, 'paused'> | null
  prompt: string
  transcript: string
  steps: AgentStepState[]
  activeStepId: string | null
  startedAt: number | null
  firstTokenMs: number | null
  error: string | null
}

export type AgentEvent =
  | {
      type: 'START'
      prompt: string
      steps: Array<{ id: string; label: string }>
      now: number
    }
  | { type: 'TOKEN'; value: string; now: number }
  | { type: 'STEP_START'; stepId: string }
  | { type: 'STEP_DONE'; stepId: string }
  | { type: 'APPLY' }
  | { type: 'VERIFY' }
  | { type: 'PAUSE' }
  | { type: 'RESUME' }
  | { type: 'COMPLETE' }
  | { type: 'CANCEL' }
  | { type: 'FAIL'; error: string }
  | { type: 'RESET' }

export const initialAgentState: AgentMachineState = {
  phase: 'idle',
  resumePhase: null,
  prompt: '',
  transcript: '',
  steps: [],
  activeStepId: null,
  startedAt: null,
  firstTokenMs: null,
  error: null,
}

const pausablePhases: AgentPhase[] = [
  'planning',
  'streaming',
  'applying',
  'verifying',
]

export function agentReducer(
  state: AgentMachineState,
  event: AgentEvent,
): AgentMachineState {
  switch (event.type) {
    case 'START':
      return {
        ...initialAgentState,
        phase: 'planning',
        prompt: event.prompt,
        steps: event.steps.map((step) => ({ ...step, status: 'queued' })),
        startedAt: event.now,
      }
    case 'TOKEN':
      if (
        state.phase === 'paused' ||
        state.phase === 'cancelled' ||
        state.phase === 'error'
      ) {
        return state
      }
      return {
        ...state,
        phase: state.phase === 'planning' ? 'streaming' : state.phase,
        transcript: state.transcript + event.value,
        firstTokenMs:
          state.firstTokenMs ??
          (state.startedAt === null ? null : event.now - state.startedAt),
      }
    case 'STEP_START':
      return {
        ...state,
        activeStepId: event.stepId,
        steps: state.steps.map((step) =>
          step.id === event.stepId ? { ...step, status: 'running' } : step,
        ),
      }
    case 'STEP_DONE':
      return {
        ...state,
        activeStepId: null,
        phase: state.phase === 'paused' ? 'paused' : 'streaming',
        steps: state.steps.map((step) =>
          step.id === event.stepId ? { ...step, status: 'done' } : step,
        ),
      }
    case 'APPLY':
      return { ...state, phase: 'applying' }
    case 'VERIFY':
      return { ...state, phase: 'verifying' }
    case 'PAUSE':
      if (!pausablePhases.includes(state.phase)) return state
      return {
        ...state,
        phase: 'paused',
        resumePhase: state.phase as Exclude<AgentPhase, 'paused'>,
      }
    case 'RESUME':
      if (state.phase !== 'paused') return state
      return {
        ...state,
        phase: state.resumePhase ?? 'streaming',
        resumePhase: null,
      }
    case 'COMPLETE':
      return {
        ...state,
        phase: 'completed',
        resumePhase: null,
        activeStepId: null,
      }
    case 'CANCEL':
      return {
        ...state,
        phase: 'cancelled',
        resumePhase: null,
        activeStepId: null,
      }
    case 'FAIL':
      return {
        ...state,
        phase: 'error',
        resumePhase: null,
        activeStepId: null,
        error: event.error,
      }
    case 'RESET':
      return initialAgentState
  }
}

export function isAgentActive(phase: AgentPhase): boolean {
  return [...pausablePhases, 'paused'].includes(phase)
}
